import { Button, ControlledInput, Modal } from "@/components";
import { ProgressBar, RadioGroup } from "@/design-system";
import { useToast } from "@/hooks";
import { trackEvent } from "@/lib/dataLayer";
import { cn } from "@/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDemoFormDraftStore } from "@/store/demoFormDraftStore";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Controller, useForm } from "react-hook-form";
import { PublicRoutes } from "@/constants/routes";
import {
  EMPTY_DEMO_LEAD,
  STORES_OPTIONS,
  demoLeadSchema as demoSchema,
  type DemoLeadValues as DemoFormValues,
} from "@/pages/landingPage/constants/demoLead";
import { useSendDemoLead } from "@/pages/landingPage/hooks/useSendDemoLead";
import { useNavigate } from "react-router";

// Etapa 1 pede só o essencial para o contato; a rede vem depois, quando o
// visitante já começou e tende a terminar.
const CONTACT_FIELDS = ["name", "phone"] as const;
const COMPANY_FIELDS = ["company", "storesRange"] as const;

type FieldName =
  | (typeof CONTACT_FIELDS)[number]
  | (typeof COMPANY_FIELDS)[number];

// Busca dentro do próprio form porque o `ControlledInput` não repassa ref ao
// input, e outra tela pode ter um campo com o mesmo id atrás do modal.
const focusField = (form: HTMLFormElement | null, field: FieldName) => {
  const selector =
    field === "storesRange" ? 'input[name="storesRange"]' : `#${field}-input`;
  form?.querySelector<HTMLInputElement>(selector)?.focus();
};

type Step = 1 | 2;

const EMPTY_VALUES = EMPTY_DEMO_LEAD;

type DemoFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Título do modal; troque quando o CTA que abre não for "demo" (ex.: teste grátis). */
  title?: string;
  description?: string;
};

export const DemoFormModal = ({
  isOpen,
  onClose,
  title = "Agende sua demo",
  description,
}: DemoFormModalProps) => {
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { saveDraft, clearDraft } = useDemoFormDraftStore();
  // Lido uma vez só: o rascunho é o ponto de partida, depois quem manda é o form.
  const [initialDraft] = useState(() => useDemoFormDraftStore.getState());
  const [step, setStep] = useState<Step>(initialDraft.step);
  const navigate = useNavigate();
  const sendDemoLead = useSendDemoLead("landing-page-hero");
  const formRef = useRef<HTMLFormElement>(null);
  // Conta a etapa 1 uma vez por abertura do modal: ir e voltar entre as
  // etapas não pode inflar o funil.
  const contactStepTracked = useRef(false);
  const { control, handleSubmit, reset, register, trigger, getValues, watch } =
    useForm<DemoFormValues>({
      resolver: zodResolver(demoSchema),
      mode: "onChange",
      defaultValues: {
        ...EMPTY_VALUES,
        name: initialDraft.name,
        phone: initialDraft.phone,
        company: initialDraft.company,
        storesRange: initialDraft.storesRange as DemoFormValues["storesRange"],
      },
    });

  // Fechar o modal não apaga nada: quem sai sem querer, ou vai ver outra parte
  // do site, volta com o que já tinha preenchido. Só o envio limpa.
  useEffect(() => {
    const subscription = watch(({ name, phone, company, storesRange }) =>
      saveDraft({
        name: name ?? "",
        phone: phone ?? "",
        company: company ?? "",
        storesRange,
      })
    );
    return () => subscription.unsubscribe();
  }, [watch, saveDraft]);

  const resetForm = () => {
    reset(EMPTY_VALUES);
    setStep(1);
    clearDraft();
  };

  const handleClose = () => {
    if (isSubmitting) return;
    contactStepTracked.current = false;
    onClose();
  };

  const changeStep = (next: Step) => {
    setStep(next);
    saveDraft({ step: next });
    // Espera a etapa aparecer antes de focar: campo com `hidden` não recebe foco.
    requestAnimationFrame(() =>
      focusField(formRef.current, next === 1 ? "name" : "company")
    );
  };

  // Botão sempre clicável: com ele desabilitado, quem não via o campo vazio
  // ficava sem entender por que não avançava. No clique, o erro aparece embaixo
  // do campo e o foco vai até ele.
  const findFirstInvalid = (fields: readonly FieldName[]) =>
    fields.find(
      field => !demoSchema.shape[field].safeParse(getValues(field)).success
    );

  const goToCompanyStep = async () => {
    if (!(await trigger(CONTACT_FIELDS))) {
      const firstInvalid = findFirstInvalid(CONTACT_FIELDS);
      if (firstInvalid) focusField(formRef.current, firstInvalid);
      return;
    }

    if (!contactStepTracked.current) {
      trackEvent({ event: "form_step_demo", step: "contact" });
      contactStepTracked.current = true;
    }
    changeStep(2);
  };

  // O envio valida as duas etapas. Se o erro for da etapa 1 (rascunho antigo
  // restaurado direto na etapa 2), volta para ela: senão o erro ficaria num
  // campo escondido e o botão pareceria não fazer nada.
  const handleInvalid = () => {
    const contactInvalid = findFirstInvalid(CONTACT_FIELDS);
    if (contactInvalid) {
      setStep(1);
      saveDraft({ step: 1 });
      requestAnimationFrame(() => focusField(formRef.current, contactInvalid));
      return;
    }
    const companyInvalid = findFirstInvalid(COMPANY_FIELDS);
    if (companyInvalid) focusField(formRef.current, companyInvalid);
  };

  const onSubmit = async (data: DemoFormValues) => {
    setIsSubmitting(true);
    try {
      await sendDemoLead(data);
      resetForm();
      contactStepTracked.current = false;
      onClose();
      navigate(PublicRoutes.BOAS_VINDAS);
    } catch (err) {
      console.error("[demo-form] submit failed", err);
      showToast(
        "Não conseguimos enviar agora. Tente novamente ou fale direto pelo WhatsApp.",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enter na etapa 1 avança em vez de enviar: o envio validaria os campos da
  // etapa 2 e mostraria erro em algo que o visitante ainda nem viu.
  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (step === 1) {
      event.preventDefault();
      void goToCompanyStep();
      return;
    }
    void handleSubmit(onSubmit, handleInvalid)(event);
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleClose}
      size="md"
      labelledById="demo-form-title"
    >
      <div className="flex flex-col gap-4 p-2">
        <header className="flex flex-col gap-1">
          <h2 id="demo-form-title" className="text-title-sm text-black">
            {title}
          </h2>
          {description && (
            <p className="text-sm text-gray-700">{description}</p>
          )}
        </header>

        <ProgressBar
          value={step}
          max={2}
          size="sm"
          label={step === 1 ? "Seus dados" : "Sua rede"}
          hint={`Etapa ${step} de 2`}
          valueText={`Etapa ${step} de 2`}
        />

        <form
          ref={formRef}
          onSubmit={handleFormSubmit}
          className="flex flex-col gap-3"
          noValidate
        >
          {/* As duas etapas ficam montadas e só a ativa aparece: desmontar os
              campos da etapa 1 fazia o formulário perder o nome. */}
          <div className={cn("flex flex-col gap-3", step !== 1 && "hidden")}>
            <ControlledInput
              control={control}
              name="name"
              label="Seu nome"
              placeholder="Como podemos te chamar?"
              autoComplete="name"
              autoFocus={initialDraft.step === 1}
            />
            <ControlledInput
              control={control}
              name="phone"
              inputType="phone"
              type="tel"
              label="WhatsApp"
              placeholder="(11) 99999-9999"
              autoComplete="tel-national"
            />
          </div>
          <div className={cn("flex flex-col gap-3", step !== 2 && "hidden")}>
            <ControlledInput
              control={control}
              name="company"
              label="Nome da rede"
              placeholder="Ex.: Lojas Modelo"
              autoComplete="organization"
              autoFocus={initialDraft.step === 2}
            />
            <Controller
              control={control}
              name="storesRange"
              render={({ field, fieldState }) => (
                <RadioGroup
                  name="storesRange"
                  label="Quantas lojas a rede tem?"
                  options={STORES_OPTIONS}
                  value={field.value ?? ""}
                  onValueChange={field.onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>

          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="hidden"
            {...register("honeypot")}
          />

          <div className="flex flex-col gap-2 pt-2 sm:flex-row-reverse">
            {step === 1 ? (
              <Button type="submit" className="rounded-full">
                Continuar
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={isSubmitting}
                isLoading={isSubmitting}
                className="rounded-full"
              >
                Falar com a Fidexa
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              onClick={step === 1 ? handleClose : () => changeStep(1)}
              disabled={isSubmitting}
              className="rounded-full"
            >
              {step === 1 ? "Cancelar" : "Voltar"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
